/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse, AxiosInstance } from "axios";
import { MaxPagedResults, NextPagedResults, PagedResults } from './Paging';
import { EventSourcePolyfill } from 'event-source-polyfill';

const DBGIS_MARKER_HEADER = "X-IsDbGIS";

let totalorder = 1;

type toDateFn = (val: any, isSeconds?: boolean) => Date | null;

function toDate(val: any, isSeconds = false): Date | null {
    if (val !== null) {
        if (typeof val === "string") {
            return new Date(val);
        }
        if (typeof val === "number") {
            return new Date(val * (isSeconds ? 1000 : 1));
        }
        console.warn(`Cannot convert ${val} of type ${typeof val} to Date`);
    }
    return null
}

function propsToDate(obj: { [key: string]: any }, keys: string[], isSeconds = false, toDateFn?: toDateFn): void {
    keys.forEach((key) => {
        const path = key.split(".");
        const lastKey = path.splice(path.length - 1, 1).join("");
        const ref = path.reduce((accum, k) => accum ? accum[k] : accum, obj);
        if (ref === undefined) {
            console.warn(`Invalid path to object, ${key} points to undefined `);
        } else if (ref !== null) {
            // Ignore null objects
            const val = ref[lastKey];
            ref[lastKey] = toDateFn ? toDateFn(val, isSeconds) : toDate(val, isSeconds);
        }
    });
}

function propsToDatePaged(obj: PagedResults<any> | MaxPagedResults<any> | NextPagedResults<any>, keys: string[], isSeconds = false, toDateFn?: toDateFn): void {
    obj.records.forEach(r => propsToDate(r, keys, isSeconds, toDateFn));
}


function resolvedAxiosPromise<T = any>(data: T): AxiosPromise<T> {
    return Promise.resolve({
        data,
        status: 200,
        statusText: "OK",
        headers: {},
        config: {}
    });
}

const defaultConfig: AxiosRequestConfig = {
    paramsSerializer(params): string {
        const parts: string[] = [];

        Object.keys(params).forEach((key) => {
            let val = params[key];
            if (val === null || typeof val === "undefined") {
                return;
            }

            const encodedKey = encodeURIComponent(key);

            if (!Array.isArray(val)) {
                val = [val];
            }

            val.forEach((v: any) => {
                if (Object.prototype.toString.call(v) === "[object Date]") {
                    v = v.toISOString();
                } else if (typeof v === "object") {
                    v = JSON.stringify(v);
                }
                parts.push(`${encodedKey}=${encodeURIComponent(v)}`);
            });

        });

        return parts.join("&");
    }
};

interface ErrorHandler {
    /**
     * @param err the error
     * @param response the axios response, if the error was an HTTP one (i.e. 4xx, 5xx)
     */
    onResponseError(err: Error, response?: AxiosResponse): void;
}

interface HTTPClientOptions {
    errorHandler?: ErrorHandler;
    axiosConfig?: AxiosRequestConfig;
    useApiSSO?: boolean;
    useApiGw?: boolean;
    forcedContext?: string;
}

type RequestConfigProvider = (config: AxiosRequestConfig) => Promise<void>;

interface RequestConfigProviderOrder {
    order: number;
    provider: RequestConfigProvider;
}

class HTTPClient {
    private axiosClient: AxiosInstance;
    private nc = new Date().getTime();
    private errorHandlers: ErrorHandler[] = [];
    private forcedContext: string;

    private configProviders: RequestConfigProviderOrder[] = [];

    constructor(config: HTTPClientOptions = {}) {
        const cfg = config.axiosConfig || defaultConfig;
        if (!cfg.paramsSerializer) {
            cfg.paramsSerializer = defaultConfig.paramsSerializer;
        }
        
        if(config.useApiGw){
            cfg.headers = {
                ... cfg.headers,
                "x-api-request": true
            };
        }

        if (config.forcedContext && config.forcedContext !== "" && !config.forcedContext.startsWith("/")) {
            throw new Error("config.forcedContext must start with /");
        }
        this.forcedContext = config.forcedContext ?? "";

        this.axiosClient = axios.create(cfg);

        if (config.errorHandler) {
            this.errorHandlers.push(config.errorHandler);
        }

        this.axiosClient.interceptors.request.use((reqConfig) => {
            const params = reqConfig.params || {};
            params._nc = this.nc++;
            reqConfig.params = params;
            if (config.useApiSSO) {
                reqConfig.url = reqConfig.url?.replace("/api_basic/", "/api_sso/");
            }

            if (this.forcedContext && reqConfig.url?.startsWith("/") && !reqConfig.url?.startsWith(this.forcedContext)) {
                reqConfig.url = this.forcedContext + reqConfig.url;
            }
    
            return reqConfig;
        });

        this.axiosClient.interceptors.response.use(
            (res) => {
                return res;
            },
            (err: any) => {
                this.notifyErrorHandlers(err, err.response);
                return Promise.reject(err);
            }
        );
    }

    private notifyErrorHandlers(err: Error, response?: AxiosResponse) {
        this.errorHandlers.forEach((h) => {
            h.onResponseError(err, response);
        });
    }

    private async provideConfig(config?: AxiosRequestConfig): Promise<AxiosRequestConfig> {
        const ret = config ? config : {};     // TODO: Deep clone config?
        for (const p of this.configProviders) {
            /* We are not firing all providers in parallel
             * to avoid the situation where 2 providers changes the same config elements
             * making debugging difficult due to possible different results at every invocation
             * we should not have more than a couple providers anyway and most of them should be synchronous
             */
            await p.provider(ret);
        }
        return ret;
    }

    private async dbgisConfig(config?: AxiosRequestConfig): Promise<AxiosRequestConfig> {
        const ret = config || {};
        ret.headers = ret.headers || {};
        ret.headers[DBGIS_MARKER_HEADER] = "Y";
        return await this.provideConfig(ret);
    }

    public addErrorHandler(handler: ErrorHandler) {
        this.errorHandlers.push(handler);
    }

    public addRequestConfigProvider(provider: RequestConfigProvider, order?: number) {
        let ord = order;
        if (!ord) {
            ord = totalorder++;
        }
        this.configProviders.push({ provider, order: ord });
        this.configProviders = this.configProviders.sort((a, b) => a.order - b.order);
    }

    public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.get(url, await this.provideConfig(config));
    }

    public async getDbGIS<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.get(url, await this.dbgisConfig(config));
    }

    public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.delete(url, await this.provideConfig(config));
    }

    public async deleteDbGIS<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.delete(url, await this.dbgisConfig(config));
    }

    public async head<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.head(url, await this.provideConfig(config));
    }

    public async headDbGIS<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.head(url, await this.dbgisConfig(config));
    }

    public async options<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.options(url, await this.provideConfig(config));
    }

    public async optionsDbGIS<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.options(url, await this.dbgisConfig(config));
    }

    public async post<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.post(url, data, await this.provideConfig(config));
    }

    public async postDbGIS<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.post(url, data, await this.dbgisConfig(config));
    }

    public async put<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.put(url, data, await this.provideConfig(config));
    }

    public async putDbGIS<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.put(url, data, await this.dbgisConfig(config));
    }

    public async patch<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.patch(url, data, await this.provideConfig(config));
    }

    public async patchDbGIS<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosClient.patch(url, data, await this.dbgisConfig(config));
    }

    public async eventSource<T = any>(url: string, config?: AxiosRequestConfig): Promise<EventSource> {
        const EventSource = EventSourcePolyfill;
        return new EventSource(url, await this.provideConfig(config) as any);
    }

    public setLanguage(lang: string) {
        this.axiosClient.defaults.headers.common['Accept-Language'] = lang;
    }
}

export {
    AxiosPromise,
    AxiosRequestConfig,
    AxiosResponse,
    AxiosInstance,
    ErrorHandler,
    HTTPClient,
    propsToDate,
    propsToDatePaged,
    toDateFn,
    RequestConfigProvider,
    resolvedAxiosPromise,
    DBGIS_MARKER_HEADER
};
